import { useState, useCallback } from 'react';
import {
    AppBar,
    Box,
    Button,
    IconButton,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WidgetsIcon from '@mui/icons-material/Widgets';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import FolderIcon from '@mui/icons-material/Folder';
import {
    EditOffOutlined,
    FileDownloadOutlined,
    FileOpenOutlined,
    SaveAsOutlined,
    SaveOutlined,
} from '@mui/icons-material';
import useStore from '../../store';
import { socket, URL } from '../../socket';
import { ProjectEvent, EngineEvent } from '../../peregrineFlowInterfaces';
import AccountMenu from './AccountMenu';
import FileManager from '../../FileBrowser/FileManager';

const AppHeader = () => {
    const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
    const currentView = useStore((state) => state.currentView);
    const project = useStore((state) => state.project);
    const nodes = useStore((state) => state.nodes);
    const edges = useStore((state) => state.edges);
    const token = useStore((state) => state.token);
    const openProjectList = useStore((state) => state.openProjectList);
    const setShowSaveAsDialog = useStore((state) => state.setShowSaveAsDialog);
    const setShowNodeProvider = useStore((state) => state.setShowNodeProvider);
    const isRunning = useStore((state) => state.isRunning);
    const lockEngine = useStore((state) => state.lockEngine);
    const setLockEngine = useStore((state) => state.setLockEngine);
    const appEdges = useStore((state) => state.appEdges);
    const isAuthenticated = useStore((state) => state.isAuthenticated);
    const isGuest = useStore((state) => state.isGuest);
    const engineRunCount = useStore((state) => state.engineRunCount);
    const hasSeenRunLimitDialog = useStore((state) => state.hasSeenRunLimitDialog);
    const incrementEngineRunCount = useStore((state) => state.incrementEngineRunCount);
    const setShowSignUpDialog = useStore((state) => state.setShowSignUpDialog);
    const [showFileManager, setShowFileManager] = useState<boolean>(false);

    const handleProjectMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElMenu(event.currentTarget);
    };

    const handleProjectMenuClose = () => {
        setAnchorElMenu(null);
    };

    const handleOpenProject = useCallback(() => {
        handleProjectMenuClose();
        openProjectList();
    }, [openProjectList]);

    const handleFileManagerOpen = useCallback(() => {
        handleProjectMenuClose();
        setShowFileManager(true);
    }, []);

    const handleFileManagerClose = useCallback(() => {
        setShowFileManager(false);
    }, []);

    const runEngineEvent = useCallback((run: boolean) => {
        setLockEngine(true);
        if (run) {
            // Get free tier limit from environment variable with default of 3
            const freeTierLimit = parseInt(process.env.REACT_APP_FREE_TIER_RUN_LIMIT || '3', 10);

            // Check if user is a guest and has reached the run limit (and hasn't seen the dialog yet)
            if (isGuest && !hasSeenRunLimitDialog && engineRunCount >= freeTierLimit) {
                setShowSignUpDialog(true, 'runLimit');
                setLockEngine(false);
                return;
            }

            // Increment run count for guest users
            if (isGuest) {
                incrementEngineRunCount();
            }

            const event = { event_type: "starting", connections: appEdges } as unknown as EngineEvent;
            socket.emit('engine', event);
        } else {
            const event = { event_type: "stopping" } as unknown as EngineEvent;
            socket.emit('engine', event);
        }
    }, [appEdges, setLockEngine, isGuest, engineRunCount, hasSeenRunLimitDialog, incrementEngineRunCount, setShowSignUpDialog]);

    const renderStartStop = useCallback(() => {
        if (currentView !== 'editor') return null;

        const commonButtonStyles = {
            position: 'absolute',
            left: '50%',
            bottom: '-35px',
            transform: 'translate(-50%, -50%)',
            border: '1px solid #cccccc',
            borderRadius: '12px',
            textTransform: 'none',
            padding: '6px 16px',
            boxShadow: 'none',
            zIndex: 1000,
            '&.Mui-disabled': {
                backgroundColor: 'rgba(128, 128, 128, 0.5)',
                color: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(128, 128, 128, 0.3)',
            },
        };

        if (isRunning) {
            return (
                <Button
                    variant="contained"
                    disabled={lockEngine}
                    onClick={() => runEngineEvent(false)}
                    startIcon={
                        <span className="running-icon" style={{ display: 'inline-flex' }}>
                            <DirectionsRunIcon />
                        </span>
                    }
                    sx={{
                        ...commonButtonStyles,
                        backgroundColor: 'var(--peregrine-secondary)',
                        '&:hover': {
                            backgroundColor: 'var(--peregrine-secondary)',
                            filter: 'brightness(0.9)',
                        },
                    }}
                >
                    Stop Workflow
                </Button>
            );
        }

        return (
            <Button
                variant="contained"
                disabled={lockEngine}
                onClick={() => runEngineEvent(true)}
                startIcon={<DirectionsRunIcon />}
                sx={{
                    ...commonButtonStyles,
                    backgroundColor: 'var(--peregrine-main)',
                    '&:hover': {
                        backgroundColor: 'var(--peregrine-main)',
                        filter: 'brightness(1.2)',
                    },
                }}
            >
                Run Workflow
            </Button>
        );
    }, [isRunning, runEngineEvent, lockEngine, currentView]);

    const updateProject = useCallback((save: boolean) => {
        if (project?.read_only) {
            return;
        }

        const layout = Object.fromEntries(nodes.map((node) => {
            const pos = { x: node.position.x, y: node.position.y };
            return [node.id, pos];
        }));

        if (project) {
            project.extra_data = { layout: layout, edges: edges };
        }
        const event = { event_type: "update", project: project, save: save } as unknown as ProjectEvent;
        socket.emit("project", event);

        handleProjectMenuClose();
    }, [project, nodes, edges]);

    const handleSaveAs = useCallback(() => {
        handleProjectMenuClose();
        setShowSaveAsDialog(true);
    }, [setShowSaveAsDialog]);

    const handleNodeProviderClick = useCallback(() => {
        setShowNodeProvider(true);
    }, [setShowNodeProvider]);

    // Project name display for editor view
    const projectTitle = currentView === 'editor' && project ? (
        <Box display="flex" alignItems="center">
            <Typography variant="h6" component="div">
                Project: {project.name}
            </Typography>
            {project.read_only && (
                <Tooltip title="Read Only">
                    <EditOffOutlined fontSize="small" color="inherit" sx={{ ml: 1, color: 'white' }} />
                </Tooltip>
            )}
            {renderStartStop()}
        </Box>
    ) : (
        <Typography variant="h6" component="div">
            Peregrine Flow
        </Typography>
    );

    return (
        <AppBar position="static">
            <Toolbar>
                {/* Left side - Editor specific actions */}
                {currentView === 'editor' && (
                    <IconButton
                        disabled={!project}
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="available nodes"
                        sx={{ mr: 2 }}
                        onClick={handleNodeProviderClick}
                    >
                        <WidgetsIcon />
                    </IconButton>
                )}

                {/* Center - Title/Project name */}
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                    {projectTitle}
                </Box>

                {/* Right side - Action buttons */}
                {currentView === 'editor' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {/* File Manager - always visible */}
                        <Tooltip title="File Manager">
                            <IconButton
                                color="inherit"
                                aria-label="file manager"
                                onClick={handleFileManagerOpen}
                            >
                                <FolderIcon />
                            </IconButton>
                        </Tooltip>

                        {/* Open Project - always visible */}
                        <Tooltip title="Open Project">
                            <IconButton
                                color="inherit"
                                aria-label="open project"
                                onClick={handleOpenProject}
                            >
                                <FileOpenOutlined />
                            </IconButton>
                        </Tooltip>

                        {/* Save - always visible */}
                        <Tooltip title="Save">
                            <span>
                                <IconButton
                                    color="inherit"
                                    aria-label="save project"
                                    onClick={() => updateProject(true)}
                                    disabled={project === null || project?.read_only}
                                >
                                    <SaveOutlined />
                                </IconButton>
                            </span>
                        </Tooltip>

                        {/* More menu for less common actions - only for authenticated users */}
                        {isAuthenticated && (
                            <>
                                <Tooltip title="More options">
                                    <IconButton
                                        color="inherit"
                                        aria-label="more options"
                                        onClick={handleProjectMenu}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    id="menu-appbar"
                                    anchorEl={anchorElMenu}
                                    keepMounted
                                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                    open={Boolean(anchorElMenu)}
                                    onClose={handleProjectMenuClose}
                                >
                            <MenuItem onClick={handleSaveAs} disabled={project === null || token === undefined}>
                                <ListItemIcon>
                                    <SaveAsOutlined fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Save as ...</ListItemText>
                            </MenuItem>
                            <MenuItem
                                component={Link}
                                href={`${URL}/download?token=${token}&kind=project&project=${project?.name}`}
                                target="_blank"
                                disabled={project === null}
                            >
                                <ListItemIcon>
                                    <FileDownloadOutlined fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Download Project</ListItemText>
                            </MenuItem>
                                </Menu>
                            </>
                        )}
                    </Box>
                )}

                {/* Account menu - always visible */}
                <AccountMenu />
            </Toolbar>
            <FileManager
                open={showFileManager}
                onClose={handleFileManagerClose}
                mode="manage"
                title="File Manager"
            />
        </AppBar>
    );
};

export default AppHeader;
